import { all, call, put, takeEvery, takeLatest, delay } from 'redux-saga/effects';
import {
    getPhaseStructuresFailed,
    getPhaseStructuresSuccess,
    getReportSummaryFailed,
    getReportSummarySuccess,
    getStructureCompareFailed,
    getStructureCompareSuccess,
    getStructureSummaryFailed,
    getStructureSummarySuccess,
    setTargets,
    updateRanges,
} from './actions';
import {
    GET_PHASE_STRUCTURES,
    GET_REPORT_SUMMARY,
    GET_STRUCTURE_COMPARE,
    GET_STRUCTURE_SUMMARY,
    UPDATE_TARGETS,
    GET_TARGETS,
} from './constants';
import {
    getCompareApi,
    getLayersApi,
    getManagersFTESApi,
    getManagersStatsApi,
    getNonManagersStatsApi,
    getSpanReportsApi,
    getTotalFTESApi,
    setTargetsApi,
    getTargetsApi,
    getAnalyticsApi,
} from './apis';
import { getAnalyticsFilterString } from '../../common/utilities';

const PAGE_SIZE = 10000;

export function* getPhaseStructures({ clientId, phaseId }) {
    try {
        const filterString = getAnalyticsFilterString({ filters: 'structures' });
        const { data } = yield call(getAnalyticsApi, { method: 'filterJson', filterString });
        const structures = {
            data: (data.structures || []).map(s => ({ id: s.id, name: s.label })),
        };
        yield put(getPhaseStructuresSuccess(structures));
    } catch ({ response }) {
        const { data: errorData = {}, status = '' } = response;
        const { errors = [] } = errorData;
        const detail = errors[0] || '';
        yield put(getPhaseStructuresFailed({ detail, status }));
    }
}

// Helper function to fetch all pages for an API
// Supports both page-based and cursor-based pagination
function* fetchAllPages(attribute, str1, str2, primaryFilters, isSummary = false) {
    let allData = [];
    let hasMore = true;
    let firstPageData = null;
    
    // N-1 and N-3 use cursor-based pagination, others use page-based
    const isCursorBased = attribute === 'n1-manager-change' || attribute === 'n3-manager-change';
    let pageOrCursor = isCursorBased ? null : 1;  // cursor starts as null, page starts as 1

    while (hasMore) {
        try {
            const { data } = yield call(
                getCompareApi,
                attribute,
                str1,
                str2,
                { primary_filters: primaryFilters },
                pageOrCursor,
                PAGE_SIZE
            );

            if (!firstPageData) {
                firstPageData = data;
            }

            if (isSummary) {
                // For summary APIs, extract the changes array
                const responseData = data.data || {};
                const changes = responseData.changes || [];
                allData = allData.concat(changes);
                hasMore = data.has_more || false;
                
                // Update pagination for next request
                if (hasMore) {
                    if (isCursorBased) {
                        pageOrCursor = responseData.next_cursor || null;
                        if (!pageOrCursor) hasMore = false;  // No cursor means no more data
                    } else {
                        pageOrCursor += 1;
                    }
                }
            } else {
                // For compare APIs, check if it's a list-based API or aggregated API
                const responseData = data.data || {};
                const key = Object.keys(responseData)[0];

                if (key) {
                    const currentData = responseData[key];
                    if (Array.isArray(currentData)) {
                        // List-based API - accumulate all pages
                        allData = allData.concat(currentData);
                        hasMore = data.has_more || false;
                        
                        // Update pagination for next request
                        if (hasMore) {
                            if (isCursorBased) {
                                pageOrCursor = responseData.next_cursor || null;
                                if (!pageOrCursor) hasMore = false;
                            } else {
                                pageOrCursor += 1;
                            }
                        }
                    } else {
                        // Aggregated API - just return first page data
                        hasMore = false;
                        return { data: responseData, isAggregated: true };
                    }
                } else {
                    hasMore = false;
                }
            }

            if (hasMore) {
                yield delay(5000);
            }
        } catch (error) {
            throw error;
        }
    }

    return { data: allData, isAggregated: false, firstPageData };
}

export function* getStructureCompareData({ attribute, str1, str2, primaryFilters }) {
    try {
        const result = yield* fetchAllPages(attribute, str1, str2, primaryFilters, false);

        let responseData = {};

        if (result.isAggregated) {
            // For aggregated APIs, use the data as-is
            responseData = result.data;
        } else {
            // For list-based APIs, wrap in the expected structure
            const key = Object.keys(result.firstPageData?.data || {})[0] || attribute;
            responseData[key] = result.data;
        }

        yield put(getStructureCompareSuccess({ data: responseData }));
    } catch ({ response }) {
        const { data: errorData = {}, status = '' } = response;
        const { errors = [] } = errorData;
        const detail = errors[0] || '';
        yield put(getStructureCompareFailed({ detail, status }));
    }
}

export function* getStructureSummary({ attribute, str1, str2, primaryFilters }) {
    try {
        const allChanges = yield* fetchAllPages(attribute, str1, str2, primaryFilters, true);

        // Get attributes from first page response
        const firstPageResponse = yield call(
            getCompareApi,
            attribute,
            str1,
            str2,
            { primary_filters: primaryFilters },
            1,
            PAGE_SIZE
        );
        const firstPageData = firstPageResponse.data.data || {};
        const attributes = firstPageData.attributes || [];

        const returnedData = {
            changes: allChanges,
            attributes: attributes,
        };

        yield put(getStructureSummarySuccess(returnedData));
    } catch ({ response }) {
        const { data: errorData = {}, status = '' } = response || { data: {}, status: '' };
        const { errors = [] } = errorData;
        const detail = errors[0] || '';
        yield put(getStructureSummaryFailed({ detail, status }));
    }
}

export function* getReportDetails({ id, primaryFilters }) {
    try {
        const [managersStats, managersFTES, nonManagersStats, totalFTES, layers, spanReports] = yield all([
            call(getManagersStatsApi, id, { primary_filters: primaryFilters }),
            call(getManagersFTESApi, id, { primary_filters: primaryFilters }),
            call(getNonManagersStatsApi, id, { primary_filters: primaryFilters }),
            call(getTotalFTESApi, id, { primary_filters: primaryFilters }),
            call(getLayersApi, id, { primary_filters: primaryFilters }),
            call(getSpanReportsApi, id, { primary_filters: primaryFilters }),
        ]);

        const reportDetails = {
            cost: {
                totalCost: totalFTES.data.data.totalCost,
                totalICCost: totalFTES.data.data.totalCost - managersStats.data.data.totalManagerCost,
                totalManagerCost: managersStats.data.data.totalManagerCost,
                totalManagerContractorCost: managersStats.data.data.totalManagerContractorCost,
                totalICContractorCost: nonManagersStats.data.data.totalICContractorCost,
            },
            ftes: {
                total: totalFTES.data.data.totalFTEs,
                totalICContractors: nonManagersStats.data.data.totalICContractors,
                totalICs: totalFTES.data.data.totalFTEs - managersFTES.data.data.totalManagers,
                totalManagerContractors: managersStats.data.data.totalManagerContractors,
                totalManagers: managersFTES.data.data.totalManagers,
            },
            stats: {
                avgSpan: spanReports.data.data.avgSpan,
                totalFTEs: totalFTES.data.data.totalFTEs,
                totalLayers: layers.data.data.totalLayers,
                uniqueJobs: layers.data.data.uniqueJobs,
                managerUnder4: spanReports.data.data.managerUnder4,
                managerUnder2: spanReports.data.data.managerUnder2,
            },
        };

        yield put(getReportSummarySuccess({ data: reportDetails }));
    } catch ({ response }) {
        const { data: errorData = {}, status = '' } = response;
        const { errors = [] } = errorData;
        const detail = errors[0] || '';
        yield put(getReportSummaryFailed({ detail, status }));
    }
}

function* getTargetsCall() {
    try {
        const clientId = localStorage.getItem('clientId');
        const phaseId = localStorage.getItem('phaseId');
        const { data } = yield call(getTargetsApi, clientId, phaseId);
        localStorage.setItem('executive_targets', JSON.stringify(data.data));
        yield put(setTargets(data.data));
        if (data?.data?.range) {
            yield put(updateRanges(data.data.range));
        }
    } catch ({ response }) {
        yield put(setTargets({}));
    }
}

function* updateTargets({ payload }) {
    try {
        const clientId = localStorage.getItem('clientId');
        const phaseId = localStorage.getItem('phaseId');
        yield call(setTargetsApi, { client_id: clientId, phase_id: phaseId, config: payload });
        yield put(setTargets(payload));
    } catch ({ response }) {
        yield put(setTargets({}));
    }
}

const structureSagas = [
    takeLatest(`${GET_PHASE_STRUCTURES}_PENDING`, getPhaseStructures),
    takeEvery(`${GET_STRUCTURE_COMPARE}_PENDING`, getStructureCompareData),
    takeEvery(`${GET_STRUCTURE_SUMMARY}_PENDING`, getStructureSummary),
    takeEvery(`${GET_REPORT_SUMMARY}_PENDING`, getReportDetails),
    takeLatest(UPDATE_TARGETS, updateTargets),
    takeLatest(GET_TARGETS, getTargetsCall),
];
export default structureSagas;
